// THE ONE PARSER FOR MachineGateway TIMESTAMPS.
//
// >>> WHY IT EXISTS AS A SHARED MODULE AND NOT AS A LOCAL COPY. <<<
// Two unrelated surfaces read the SAME gateway API and both need the same
// correction: the internal Edge Live Monitor (internal/edgeMonitor/) and the
// Machinery v2 machine profile's Prehľad tab (customer/machinery/v2/profile/
// prehlad/). The rule below is a property of the WIRE, not of either screen, so
// a screen-local copy is a second place for one API's contract to be stated —
// and the second copy is the one that gets forgotten when the wire changes.
//
// ============================================================
//  THE DEFECT IT CORRECTS — VERIFIED IN THE SERVER SOURCE
// ============================================================
// The gateway's customer DTOs declare bare `DateTime`, not `DateTimeOffset`:
//   GoToStockServer/src/GoToStock/MachineGateway/GoToStock.MachineGateway.WebAPI/
//     Areas/Customer/Models/MachineStatusDto.cs — `MachineEventDto.Timestamp`
//   ...Domain/MachineEvents/MachineEvent.cs:14 — the entity behind it
// The column is SQL `datetime2`, which EF materialises with
// `DateTimeKind.Unspecified`, and System.Text.Json serialises an Unspecified
// DateTime with NO `Z` and NO offset — e.g. "2026-05-08T07:30:53.4262801".
// `new Date(...)` / `Date.parse(...)` read a timezone-less ISO string as LOCAL
// time, so every such value lands shifted by the reader's UTC offset (+2h in
// CEST): an alarm raised ten minutes ago prints two hours in the FUTURE, and
// yesterday-evening rows bucket as "today".
//
// The gateway's BOT paths already avoid this by emitting a real offset
// (`new DateTimeOffset(DateTime.SpecifyKind(utc, DateTimeKind.Utc))` in
// BotEventsController.cs:290 and RecentActivityService.cs:387). The CUSTOMER
// event path does not, which is precisely why the correction has to live on
// the client — and why it must live in exactly one place there.
//
// FORWARD-COMPATIBLE ON PURPOSE. The `Z`/offset test means that the day the
// customer DTOs are switched to `DateTimeOffset` (or the mapping starts calling
// `SpecifyKind`), this function becomes a pass-through with no client change
// and no double correction. Do not "simplify" it to an unconditional `+ "Z"`.

/**
 * Parse a MachineGateway ISO timestamp as UTC and return epoch milliseconds.
 *
 * Appends `Z` only when the string carries no timezone marker of its own, so a
 * value that already states its zone is honoured verbatim.
 *
 * Returns `null` for a missing OR unparseable value — never `NaN`. A NaN
 * leaking into a sort comparator makes it inconsistent, and V8 may then permute
 * rows the comparator never even looked at; callers coerce the `null` to their
 * own sentinel instead.
 */
export const parseUtcLoose = (iso: string | null | undefined): number | null => {
  if (!iso) return null;
  const hasTz = /(Z|[+-]\d{2}:?\d{2})$/.test(iso);
  const ms = Date.parse(hasTz ? iso : iso + "Z");
  return Number.isNaN(ms) ? null : ms;
};

export default parseUtcLoose;
